/**
 * ReportsMonthNav — month stepper + free month picker for the Reports page.
 *
 * - Prev/next arrows step one month (RTL-aware: older month is on the right).
 * - Clicking the title opens a compact month-grid popover that allows ANY
 *   past month (missing report docs get lazily generated by the page).
 * - Future months are disabled.
 * - Shows "עודכן לאחרונה" from the report doc's meta.computedAt.
 */

import { useEffect, useRef, useState } from "react";
import { CaretLeft, CaretRight, CaretDown } from "@phosphor-icons/react";
import { formatTimestampDateTime } from "../MonthlyReportContent";
import "./ReportsMonthNav.css";

/** Current year/month in Asia/Jerusalem. */
export const getIsraelNowYearMonth = () => {
  // en-CA yields YYYY-MM-DD
  const s = new Date().toLocaleDateString("en-CA", {
    timeZone: "Asia/Jerusalem",
  });
  const [year, month] = s.split("-").map(Number);
  return { year, month };
};

const HEBREW_MONTHS = [
  "ינואר",
  "פברואר",
  "מרץ",
  "אפריל",
  "מאי",
  "יוני",
  "יולי",
  "אוגוסט",
  "ספטמבר",
  "אוקטובר",
  "נובמבר",
  "דצמבר",
];

const formatMonthTitle = (year, month) =>
  new Date(Date.UTC(year, month - 1, 1, 12)).toLocaleDateString("he-IL", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

const MIN_YEAR = 2020;

const ReportsMonthNav = ({ year, month, onChange, computedAt }) => {
  const { year: nowYear, month: nowMonth } = getIsraelNowYearMonth();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(year);
  const wrapRef = useRef(null);

  // Keep the picker's year in sync when the selected month changes externally.
  useEffect(() => {
    setPickerYear(year);
  }, [year]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setPickerOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pickerOpen]);

  const isCurrentMonth = year === nowYear && month === nowMonth;

  const goOlder = () => {
    const m = month === 1 ? 12 : month - 1;
    const y = month === 1 ? year - 1 : year;
    if (y >= MIN_YEAR) onChange(y, m);
  };

  const goNewer = () => {
    if (isCurrentMonth) return;
    const m = month === 12 ? 1 : month + 1;
    const y = month === 12 ? year + 1 : year;
    onChange(y, m);
  };

  const isFutureMonth = (y, m) => y > nowYear || (y === nowYear && m > nowMonth);

  const pickMonth = (m) => {
    if (isFutureMonth(pickerYear, m)) return;
    onChange(pickerYear, m);
    setPickerOpen(false);
  };

  return (
    <div className="rmn-nav" ref={wrapRef}>
      <div className="rmn-row">
        {/* Older month — on the right in RTL */}
        <button
          type="button"
          className="rmn-arrow-btn"
          onClick={goOlder}
          disabled={year <= MIN_YEAR && month === 1}
          aria-label="חודש קודם"
        >
          <CaretRight size={20} weight="bold" />
        </button>

        <button
          type="button"
          className="rmn-title-btn"
          onClick={() => setPickerOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={pickerOpen}
        >
          <span className="rmn-title">{formatMonthTitle(year, month)}</span>
          <CaretDown
            size={14}
            weight="bold"
            className={`rmn-caret ${pickerOpen ? "rmn-caret-open" : ""}`}
          />
        </button>

        {/* Newer month — on the left in RTL */}
        <button
          type="button"
          className="rmn-arrow-btn"
          onClick={goNewer}
          disabled={isCurrentMonth}
          aria-label="חודש הבא"
        >
          <CaretLeft size={20} weight="bold" />
        </button>
      </div>

      {computedAt && (
        <div className="rmn-updated-at">
          עודכן לאחרונה: {formatTimestampDateTime(computedAt)}
        </div>
      )}

      {pickerOpen && (
        <div className="rmn-popover" role="dialog" aria-label="בחירת חודש">
          <div className="rmn-year-row">
            <button
              type="button"
              className="rmn-arrow-btn"
              onClick={() => setPickerYear((y) => Math.max(MIN_YEAR, y - 1))}
              disabled={pickerYear <= MIN_YEAR}
              aria-label="שנה קודמת"
            >
              <CaretRight size={16} weight="bold" />
            </button>
            <span className="rmn-year-label">{pickerYear}</span>
            <button
              type="button"
              className="rmn-arrow-btn"
              onClick={() => setPickerYear((y) => Math.min(nowYear, y + 1))}
              disabled={pickerYear >= nowYear}
              aria-label="שנה הבאה"
            >
              <CaretLeft size={16} weight="bold" />
            </button>
          </div>
          <div className="rmn-month-grid">
            {HEBREW_MONTHS.map((name, i) => {
              const m = i + 1;
              const disabled = isFutureMonth(pickerYear, m);
              const selected = pickerYear === year && m === month;
              return (
                <button
                  key={m}
                  type="button"
                  className={`rmn-month-btn ${
                    selected ? "rmn-month-selected" : ""
                  }`}
                  onClick={() => pickMonth(m)}
                  disabled={disabled}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsMonthNav;
